import React from 'react';
import { renderStatusText } from '../CombatPlayerbar/statusWordGlyphs';
import './CanonicalEffectChip.css';

/**
 * CanonicalEffectChip — the single consolidated green/red effect chip for tooltip
 * ledgers. It is the SAME chip chassis as the in-combat result pill
 * (.batch-result-pill--*) and the keystone-animation outcome chips: an accent
 * stripe (border-left) + 90deg accent gradient + inset accent ring, carrying the
 * resource's canonical status-pill glyph.
 *
 * Default face = the resource glyph + word (e.g. a HeartGlyph + "Heal", a lactic
 * glyph + "Lactic"), rendered through the real `renderStatusText` engine so it
 * reads identically to the HUD tag. ON HOVER / FOCUS the chip reveals a small
 * popover showing ONLY the concise number + magnitude of the effect (e.g.
 * "+5 HP", "+1 lactic") — the at-a-glance ledger value, engine-verified.
 *
 * Props:
 *   tone:   'pos' (green / benefit) | 'neg' (red / cost) | 'good' (a keystone's
 *           signature reward chip — uses the keystone's own accent, see `accent`)
 *   word:   the resource word shown by default (rendered via renderStatusText so
 *           it picks up the canonical glyph + color, e.g. "Heal", "Lactic")
 *   detail: the concise number + magnitude revealed on hover (e.g. "+5 HP")
 *   accent: optional "r, g, b" string — for tone 'good', the keystone's OWN accent
 *           (vancomycin "239, 68, 68" / adalimumab "96, 165, 250"); defaults to
 *           metformin teal (45, 212, 160) when omitted, so the chip matches that
 *           keystone's animation reward pill / node color.
 *
 * The popover floats ABOVE the chip; in the tooltip the ledger sits at the bottom
 * of its section so the popover rises into the section body (never out of bounds).
 */
export default function CanonicalEffectChip({ tone, word, detail, accent, signature }) {
  // Signature variant (a keystone's reward chip, flagged `signature`): render a
  // FAITHFUL scaled replica of the in-combat result pill (.batch-result-pill--
  // good-outcome / --vanc-crit) and the beat animation's .kba__go-pill — full
  // width, label LEFT (flex:1), value INLINE on the right (NOT hidden behind
  // hover), so the tooltip chip reads identically to how the effect resolves on
  // the combat interaction page. Magnitude-less reward (a still-fluid mechanic)
  // renders label-only in the same chassis.
  if (signature) {
    return (
      <span
        className={`tt-effect-chip tt-effect-chip--${tone} tt-effect-chip--signature`}
        style={accent ? { '--tt-accent': accent } : undefined}
        aria-label={detail ? `${word}: ${detail}` : word}
      >
        <span className="tt-effect-chip__face" aria-hidden="true">
          {renderStatusText(word, { iconSize: 11 })}
        </span>
        {detail && (
          <span className="tt-effect-chip__value" aria-hidden="true">
            {renderStatusText(detail, { iconSize: 11 })}
          </span>
        )}
      </span>
    );
  }
  return (
    <span
      className={`tt-effect-chip tt-effect-chip--${tone}`}
      style={accent ? { '--tt-accent': accent } : undefined}
      tabIndex={0}
      aria-label={detail ? `${word}: ${detail}` : word}
    >
      <span className="tt-effect-chip__face" aria-hidden="true">
        {renderStatusText(word, { iconSize: 11 })}
      </span>
      {detail && (
        <span className="tt-effect-chip__detail" role="tooltip" aria-hidden="true">
          {renderStatusText(detail, { iconSize: 11 })}
        </span>
      )}
    </span>
  );
}
